import type { RouteMeta, RouteRecordRaw } from 'vue-router'

import { keys } from '@/app/i18n'
import type { BreadcrumbItem, SharedViewMeta } from '@/app/router/meta'

/**
 * The engagement module's routes (WP-14, wp/engagement §3): the 10 `hr` routes (`en` until 2026-11-03, when Engagement joined Human resources) and the 4 `em` routes of the manifest,
 * paths and names from it (D-15); the legacy paths (`/hr/surveys/view/:id`, `/hr/survey-templates`,
 * `/hr/surveys-questions/edit/:id`, …) reach them through the generated legacy redirects, `new` included.
 *
 * One view per screen serves both sides (§1.1): the variant follows the user type (an employee is a Freelancer), the
 * links follow the side (`meta.software`). NEW-28: legacy carried `module` / `mode` that nothing enforced — the guard
 * checks `resource` (+ `action: 'write'` on the three forms, D-21).
 *
 * OPEN-1 (a) (queue #17): an employee opens `view-survey` (the e-mailed `/hr/surveys/view/{id}` lands there),
 * `result-survey`, `view-suggestion` and the suggestion form in the employee layout — `sharedWith: ['em']` (the guard's
 * software check, `resolveSoftware()`); every other `/en` route stays refused to them (D-21). Their crumbs and nav
 * entry are the employee area's (`sharedView.em`: `/em/surveys`, `/em/suggestions` — queue #28).
 */
const crumb = (text: string, to?: string): BreadcrumbItem => (to ? { text, to } : { text, active: true })

const surveys = crumb(keys.t('common.nav.surveys'), '/hr/surveys')
const surveyList = crumb(keys.t('engagement.surveyList.title.surveyList'), '/hr/surveys')
const emSurveys = crumb(keys.t('common.nav.surveys'), '/em/surveys')
const emSurveyList = crumb(keys.t('engagement.surveyList.title.surveyList'), '/em/surveys')
const questionList = crumb(keys.t('engagement.questionList.title.questionList'), '/hr/survey-questions')
const suggestionList = crumb(keys.t('engagement.suggestionList.title.suggestionList'), '/hr/suggestions')
const emSuggestionList = crumb(keys.t('engagement.suggestionList.title.suggestionList'), '/em/suggestions')

/** The employee side's crumbs, shared by the `/em` routes and the `/hr` routes employees open (`sharedView.em`). */
const surveyCrumb = crumb(keys.t('engagement.surveyEdit.title.survey'))
const resultsCrumb = crumb(keys.t('engagement.surveyResults.title.page'))
const suggestionCrumb = crumb(keys.t('engagement.suggestionEdit.title.suggestion'))
const EM_SURVEY_VIEW: SharedViewMeta = {
  breadcrumb: [emSurveys, emSurveyList, surveyCrumb],
  navActiveLink: 'em-surveys',
}
const EM_SURVEY_RESULTS: SharedViewMeta = { breadcrumb: [emSurveyList, resultsCrumb], navActiveLink: 'em-surveys' }
const EM_SUGGESTION: SharedViewMeta = {
  breadcrumb: [emSuggestionList, suggestionCrumb],
  navActiveLink: 'em-suggestions',
}

const SurveyListView = () => import('./views/SurveyListView.vue')
const SurveyEditView = () => import('./views/SurveyEditView.vue')
const SurveyView = () => import('./views/SurveyView.vue')
const SurveyResultsView = () => import('./views/SurveyResultsView.vue')
const QuestionEditView = () => import('./views/QuestionEditView.vue')
const SuggestionListView = () => import('./views/SuggestionListView.vue')
const SuggestionEditView = () => import('./views/SuggestionEditView.vue')

const surveyEditMeta: RouteMeta = {
  software: 'hr',
  resource: 'surveys',
  action: 'write',
  navActiveLink: 'surveys',
  pageTitle: keys.t('engagement.surveyEdit.title.page'),
  breadcrumb: [surveys, surveyList, surveyCrumb],
}

const questionEditMeta: RouteMeta = {
  software: 'hr',
  resource: 'surveys',
  action: 'write',
  navActiveLink: 'survey-questions',
  pageTitle: keys.t('engagement.questionEdit.title.page'),
  breadcrumb: [surveys, questionList, crumb(keys.t('engagement.questionEdit.title.breadcrumb'))],
}

/** Employees open these `/hr` routes too, in their own layout (OPEN-1 (a), queue #17). */
const EMPLOYEES_TOO = ['em'] as const

/** The suggestion form mounts `DocumentPicker` (`src/shared/documents`), whose strings are `documents.*` (queue #8). */
const suggestionEditMeta: RouteMeta = {
  software: 'hr',
  sharedWith: EMPLOYEES_TOO,
  resource: 'suggestions',
  action: 'write',
  navActiveLink: 'suggestions',
  catalogues: ['documents'],
  pageTitle: keys.t('engagement.suggestionEdit.title.page'),
  breadcrumb: [suggestionList, suggestionCrumb],
  sharedView: { em: EM_SUGGESTION },
}

const routes: RouteRecordRaw[] = [
  // --- surveys (employer) --------------------------------------------------------------------------------------------
  {
    path: '/hr/surveys',
    name: 'surveys',
    component: SurveyListView,
    meta: {
      software: 'hr',
      resource: 'surveys',
      pageTitle: keys.t('common.nav.surveys'),
      breadcrumb: [surveys, crumb(keys.t('engagement.surveyList.title.surveyList'))],
    },
  },
  {
    // §1.2: the same list in template mode (`templates=1`).
    path: '/hr/surveys/templates',
    name: 'survey-templates',
    component: SurveyListView,
    meta: {
      software: 'hr',
      resource: 'surveys',
      pageTitle: keys.t('common.nav.surveyTemplates'),
      breadcrumb: [surveys, crumb(keys.t('common.nav.surveyTemplates'))],
    },
  },
  { path: '/hr/surveys/new', component: SurveyEditView, meta: surveyEditMeta },
  { path: '/hr/surveys/:id(\\d+)/edit', name: 'create-edit-survey', component: SurveyEditView, meta: surveyEditMeta },
  {
    path: '/hr/surveys/:id(\\d+)',
    name: 'view-survey',
    component: SurveyView,
    meta: {
      software: 'hr',
      sharedWith: EMPLOYEES_TOO,
      sharedView: { em: EM_SURVEY_VIEW },
      resource: 'surveys',
      navActiveLink: 'surveys',
      pageTitle: keys.t('engagement.surveyView.title.page'),
      breadcrumb: [surveys, surveyList, surveyCrumb],
    },
  },
  {
    path: '/hr/surveys/:id(\\d+)/results',
    name: 'result-survey',
    component: SurveyResultsView,
    meta: {
      software: 'hr',
      sharedWith: EMPLOYEES_TOO,
      sharedView: { em: EM_SURVEY_RESULTS },
      resource: 'surveys',
      navActiveLink: 'surveys',
      pageTitle: keys.t('engagement.surveyResults.title.page'),
      breadcrumb: [surveys, surveyList, resultsCrumb],
    },
  },

  // --- question bank -------------------------------------------------------------------------------------------------
  {
    path: '/hr/survey-questions',
    name: 'survey-questions',
    component: () => import('./views/QuestionListView.vue'),
    meta: {
      software: 'hr',
      resource: 'surveys',
      pageTitle: keys.t('engagement.questionList.title.page'),
      breadcrumb: [surveys, crumb(keys.t('engagement.questionList.title.questionList'))],
    },
  },
  { path: '/hr/survey-questions/new', component: QuestionEditView, meta: questionEditMeta },
  {
    path: '/hr/survey-questions/:id(\\d+)/edit',
    name: 'create-edit-question',
    component: QuestionEditView,
    meta: questionEditMeta,
  },

  // --- suggestions ---------------------------------------------------------------------------------------------------
  {
    path: '/hr/suggestions',
    name: 'suggestions',
    component: SuggestionListView,
    meta: {
      software: 'hr',
      resource: 'suggestions',
      pageTitle: keys.t('common.nav.suggestions'),
      breadcrumb: [crumb(keys.t('engagement.suggestionList.title.suggestionList'))],
    },
  },
  { path: '/hr/suggestions/new', component: SuggestionEditView, meta: suggestionEditMeta },
  {
    path: '/hr/suggestions/:id(\\d+)/edit',
    name: 'create-edit-suggestion',
    component: SuggestionEditView,
    meta: suggestionEditMeta,
  },
  {
    path: '/hr/suggestions/:id(\\d+)',
    name: 'view-suggestion',
    component: () => import('./views/SuggestionView.vue'),
    meta: {
      software: 'hr',
      sharedWith: EMPLOYEES_TOO,
      sharedView: { em: EM_SUGGESTION },
      resource: 'suggestions',
      navActiveLink: 'suggestions',
      pageTitle: keys.t('engagement.suggestionView.title.page'),
      breadcrumb: [suggestionList, suggestionCrumb],
    },
  },

  // --- employee side (`em`, §4.11–§4.14) -----------------------------------------------------------------------------
  {
    path: '/em/surveys',
    name: 'em-surveys',
    component: SurveyListView,
    meta: {
      software: 'em',
      resource: 'surveys',
      pageTitle: keys.t('common.nav.surveys'),
      breadcrumb: [crumb(keys.t('common.nav.surveys'))],
    },
  },
  {
    path: '/em/surveys/:id(\\d+)',
    name: 'em-view-survey',
    component: SurveyView,
    meta: {
      software: 'em',
      resource: 'surveys',
      pageTitle: keys.t('engagement.surveyView.title.page'),
      ...EM_SURVEY_VIEW,
    },
  },
  {
    path: '/em/surveys/:id(\\d+)/results',
    name: 'em-result-survey',
    component: SurveyResultsView,
    meta: {
      software: 'em',
      resource: 'surveys',
      pageTitle: keys.t('engagement.surveyResults.title.page'),
      ...EM_SURVEY_RESULTS,
    },
  },
  {
    path: '/em/suggestions',
    name: 'em-suggestions',
    component: SuggestionListView,
    meta: {
      software: 'em',
      resource: 'suggestions',
      pageTitle: keys.t('common.nav.suggestions'),
      breadcrumb: [crumb(keys.t('common.nav.suggestions'))],
    },
  },
]

export default routes
